import { useCallback, useEffect, useState } from "react";
import type { LeoInstantaneResponse } from "@shared/types";
import { ApiError } from "../../lib/api";
import { getInstantane } from "../../lib/leo";
import { useLeoStore } from "../../stores/leo";
import { LeoChat } from "./leo-chat";

/**
 * Léo, l'assistant : une bulle collée en bas à droite sur toutes les pages.
 * Elle ouvre un panneau de conversation (en bas à droite sur PC, fiche
 * centrée plein écran sur mobile). Un point lime sur la bulle signale un
 * appel en cours.
 */
export function LeoWidget() {
  const open = useLeoStore((s) => s.open);
  const setOpen = useLeoStore((s) => s.setOpen);
  const [snap, setSnap] = useState<LeoInstantaneResponse | null>(null);
  const [snapErreur, setSnapErreur] = useState<string | null>(null);

  const charger = useCallback((force = false) => {
    getInstantane(force)
      .then((s) => {
        setSnap(s);
        setSnapErreur(null);
      })
      .catch((err: unknown) => setSnapErreur(err instanceof ApiError && err.status === 502 ? "Une ligne ne répond pas." : "Données indisponibles."));
  }, []);

  // Chiffres rafraîchis toutes les minutes panneau ouvert, toutes les trois minutes sinon
  // (la bulle signale les appels en cours même fermée).
  useEffect(() => {
    charger();
    const t = setInterval(() => {
      if (!document.hidden) charger();
    }, open ? 60_000 : 180_000);
    return () => clearInterval(t);
  }, [charger, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  // Sur mobile le panneau couvre l'écran : le fond ne doit pas défiler derrière.
  useEffect(() => {
    if (!open) return;
    const mobile = window.matchMedia("(max-width: 640px)").matches;
    if (!mobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const enCours = snap?.kpis.enCours ?? 0;

  return (
    <>
      <div className={`leo-backdrop${open ? " is-open" : ""}`} onClick={() => setOpen(false)} aria-hidden="true" />
      <div className={`leo-panel${open ? " is-open" : ""}`} role="dialog" aria-modal="true" aria-label="Léo, l'assistant" aria-hidden={!open}>
        {open ? <LeoChat snap={snap} snapErreur={snapErreur} onRefresh={() => charger(true)} onClose={() => setOpen(false)} /> : null}
      </div>
      {/* Bulle repliée : la pastille seule ; le nom apparaît au survol. */}
      <button type="button" className={`leo-fab${open ? " is-open" : ""}${enCours ? " is-live" : ""}`} onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "Fermer Léo" : "Parler à Léo"}>
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          <span className="leo-fab__mark" aria-hidden="true">
            <img src="/logo_mark.png" alt="" width="28" height="28" />
          </span>
        )}
        <span className="leo-fab__tip" aria-hidden="true">
          {enCours ? `Léo · ${enCours} appel${enCours > 1 ? "s" : ""} en cours` : "Léo, l'assistant"}
        </span>
        {enCours && !open ? <i className="leo-fab__live" aria-hidden="true" /> : null}
      </button>
    </>
  );
}
